import { WarningCircle, Prohibit, PersonSimpleRun } from '@phosphor-icons/react';
import React from 'react';

// Modal that warns the user about an unsafe training goal without blocking it.
// Two visual variants:
//   - WARNING (running-shoe icon, friendly blue tone) — the user can stretch
//     and might pull it off, but we want them to know it's a stretch.
//   - CRITICAL (⚠️ icon, red/orange tone) — injury risk is real; we push
//     harder, but still let them continue.
//
// Props:
//   result        — output of validateUserGoal({status, suggestedDistance, weeklyKm, distance, targetTime, reason})
//   onAdoptSuggestion()    — user picked the safer goal (called only when suggestedDistance exists)
//   onContinueAnyway()     — user insists; caller should set override flag in DB
//   onClose()              — soft dismiss (cancel form save)
export default function GoalGuardrailModal({ result, onAdoptSuggestion, onContinueAnyway, onClose }) {
  if (!result || result.status === 'ok') return null;

  const isCritical = result.status === 'critical';
  const hasSuggestion = !!result.suggestedDistance;
  // hardBlock comes from the validator (currently: full marathon
  // with < 30 km/week). When true the user CANNOT override — only
  // 'adopt the safer goal' or 'close'. No 'continue anyway' button.
  const isHardBlock = !!result.hardBlock;

  const title = isHardBlock
    ? 'יעד לא בטוח — לא ניתן להמשיך'
    : isCritical ? 'רגע לפני שיוצאים לדרך...' : 'המטרה שלך מאתגרת!';
  const Icon = isHardBlock ? Prohibit : isCritical ? WarningCircle : PersonSimpleRun;

  const headerBg = isCritical
    ? 'bg-surface border-border'
    : 'bg-surface border-border';

  const adoptBtn = 'btn-primary';

  const continueBtn = isCritical
    ? 'border-border text-text-body hover:bg-pressed'
    : 'border-border text-text-body hover:bg-pressed';

  const adoptLabel = hasSuggestion
    ? (isCritical
        ? `סגור תוכנית ל-${result.suggestedDistance}`
        : 'עדכן ליעד מומלץ')
    : 'הבנתי, אבנה נפח לפני';

  const continueLabel = isCritical
    ? 'הבנתי, אני רוצה להמשיך ליעד המקורי בכל זאת'
    : 'השאר את היעד המקורי שלי';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" dir="rtl">
      <div className="w-full max-w-md rounded-2xl bg-bg border border-border overflow-hidden">
        {/* Header */}
        <div className={`px-5 py-4 border-b ${headerBg}`}>
          <div className="flex items-center gap-2">
            <Icon size={26} weight="bold" className="text-text-body" aria-hidden="true" />
            <h2 className="text-text text-lg font-bold leading-tight">{title}</h2>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-3 text-sm">
          <p className="text-text leading-relaxed">
            {isCritical
              ? 'אנחנו פה כדי שתגיע בריא ליעד. הנתונים שלך מצביעים על פער מהותי בין המטרה לבסיס האימוני הנוכחי:'
              : 'אנחנו לצידך, רק רוצים לוודא שאתה רואה את התמונה המלאה לפני שמתחילים:'}
          </p>

          {/* Snapshot panel */}
          <div className="rounded-xl bg-pressed border border-border p-3 space-y-1.5">
            {result.distance && (
              <div className="flex justify-between">
                <span className="text-text-muted">מרחק יעד:</span>
                <span className="text-text font-bold">{result.distance}</span>
              </div>
            )}
            {result.targetTime && (
              <div className="flex justify-between">
                <span className="text-text-muted">זמן יעד:</span>
                <span className="text-text font-bold">{result.targetTime}</span>
              </div>
            )}
            {result.weeklyKm != null && (
              <div className="flex justify-between">
                <span className="text-text-muted">נפח שבועי נוכחי:</span>
                <span className="text-text font-bold">{result.weeklyKm} ק"מ</span>
              </div>
            )}
          </div>

          {/* Reason */}
          <p className="text-text-body leading-relaxed">{result.reason}</p>

          {hasSuggestion && (
            <p className="text-text-body leading-relaxed">
              ההמלצה שלנו: <span className="font-bold">{result.suggestedDistance}</span>
              {' '} — יעד ריאלי לבסיס שלך כרגע, וקרש קפיצה בטוח למרחקים הבאים.
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="px-5 py-4 border-t border-border space-y-2">
          {hasSuggestion && (
            <button
              type="button"
              onClick={onAdoptSuggestion}
              className={`w-full py-3 rounded-xl ${adoptBtn} transition`}
            >
              {adoptLabel}
            </button>
          )}
          {/* HARD BLOCK: hide 'continue anyway' so the user can ONLY
              downgrade or cancel. Marathon < 30 km/week is the
              current trigger — a real injury-risk floor. */}
          {!isHardBlock && (
            <button
              type="button"
              onClick={onContinueAnyway}
              className={`w-full py-3 rounded-xl border ${continueBtn} transition text-sm`}
            >
              {continueLabel}
            </button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="w-full py-2 text-xs text-text-muted hover:text-text-body transition"
            >
              {isHardBlock ? 'סגור' : 'ביטול'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
